import { useEffect, useMemo, useState } from 'react';
import { Select } from 'antd';
import { useParams } from 'react-router-dom';
import {
  useProcInstanceContext,
  useUserProcStepContext,
} from '@/entities/proc';
import { useProcComponentFill } from '@/shared/api';
import { useDataInvalidation } from '@/shared/hooks';
import { UserInstanceParams } from '@/shared/types';
import { captureError } from '@/shared/utils/error-reporting';

type Option = {
  value: string;
  label: string;
};

type Props<TOption> = {
  id: string;
  value: string;
  placeholder?: string;
  options: TOption[];
};

export const SelectInputMode = <TOption extends Option>(
  props: Props<TOption>,
) => {
  console.log('SelectInputMode', props);
  const invalidate = useDataInvalidation();
  const [val, setVal] = useState(props.value);
  const { code } = useParams<UserInstanceParams>();
  const { procInstance } = useProcInstanceContext();
  const { stepId } = useUserProcStepContext();

  const { mutate: updateProcComponent } = useProcComponentFill();
  const queryKey = useMemo(
    () =>
      code
        ? ['instances', procInstance?.id, { code }]
        : ['instances', procInstance?.id],
    [code, procInstance?.id],
  );

  const handleSubmit = (value: string) => {
    setVal(value);
    if (procInstance?.id && stepId) {
      updateProcComponent(
        {
          pathParams: { instanceId: procInstance?.id, stepId },
          queryParams: { code },
          body: { ...props, value },
        },
        {
          onSuccess: () => invalidate(queryKey),
          onError: (error) =>
            captureError(error, {
              module: 'select_component',
              action: 'submit_value',
            }),
        },
      );
    }
  };

  useEffect(() => {
    setVal(props.value);
  }, [props.value]);

  return (
    <Select
      data-testid="select-component"
      value={val}
      placeholder={props.placeholder}
      onSelect={(value) => handleSubmit(value)}
      options={props.options}
      style={{ width: 'fit-content', minWidth: 250, maxWidth: '100%' }}
    />
  );
};
